import { RequirePermission } from 'components/auth/RequirePermission';
import { ManagementLayout } from './Management.layout';
import './Users/translations';
import { breadcrumbTranslation } from 'components/layout/Breadcrumb/utils';
import { AssetBreadcrumb } from 'components/layout/DynamicBreadcrumbs/AssetBreadcrumb';
import { DeviceModelBreadcrumb } from 'components/layout/DynamicBreadcrumbs/DeviceModelBreadcrumb';
import { EquipmentBreadcrumb } from 'components/layout/DynamicBreadcrumbs/EquipmentBreadcrumb';
import { FleetBreadcrumb } from 'components/layout/DynamicBreadcrumbs/FleetBreadcrumb';
import { GreenerEyeBreadcrumb } from 'components/layout/DynamicBreadcrumbs/GreenerEyeBreadcrumb';
import { ManagedOrganisationBreadcrumb } from 'components/layout/DynamicBreadcrumbs/ManagedOrganisationBreadcrumb';
import { OrganisationBreadcrumb } from 'components/layout/DynamicBreadcrumbs/OrganisationBreadcrumb';
import { UserBreadcrumb } from 'components/layout/DynamicBreadcrumbs/UserBreadcrumb';
import { WatcherTypeBreadcrumb } from 'components/layout/DynamicBreadcrumbs/WatcherTypeBreadcrumb';
import { Component as AddGreenerEye } from 'pages/Management/GreenerEyes/AddGreenerEye';
import { Component as AddWatcherType } from 'pages/Management/WatcherTypes/AddWatcherType';

export const managementRoutes = [
  {
    path: '/',
    breadcrumb: null,
    element: <ManagementLayout />,
    children: [
      {
        path: 'account',
        breadcrumb: breadcrumbTranslation('management-account'),
        lazy: () => import('pages/Management/Users/MyAccount'),
      },
      {
        path: 'users',
        breadcrumb: breadcrumbTranslation('management-users'),
        lazy: () => import('pages/Management/Users'),
      },
      {
        path: 'users/:userId',
        breadcrumb: UserBreadcrumb,
        lazy: () => import('pages/Management/Users/[User]'),
      },
      {
        path: 'users/add-user',
        breadcrumb: breadcrumbTranslation('management-add-user'),
        lazy: () => import('pages/Management/Users/AddUser'),
      },
      {
        path: 'user-roles',
        breadcrumb: breadcrumbTranslation('management-user-roles'),
        lazy: () => import('pages/Management/UserRoles'),
      },
      {
        path: 'user-roles/:userRoleExtId',
        breadcrumb: ({ match }) => match.params.userRoleExtId,
        lazy: () => import('pages/Management/UserRoles/[UserRole]'),
      },
      {
        path: 'organisations',
        breadcrumb: breadcrumbTranslation('management-organisations'),
        lazy: () => import('pages/Management/Organisations'),
      },
      {
        path: 'organisations/add-organisation',
        breadcrumb: breadcrumbTranslation('management-add-organisation'),
        lazy: () => import('pages/Management/Organisations/AddOrganisation'),
      },
      {
        path: 'organisations/:managedOrganisationId/add-organisation',
        breadcrumb: breadcrumbTranslation('management-add-organisation'),
        lazy: () => import('pages/Management/Organisations/AddOrganisation'),
      },
      {
        path: 'organisations/:organisationId/:tab?',
        breadcrumb: ({ match }) => {
          const tab = match.params.tab;

          if (tab === 'users') {
            return breadcrumbTranslation('management-organisation-users')();
          }

          if (tab === 'notifications') {
            return breadcrumbTranslation('management-organisation-notifications')();
          }

          if (tab === 'equipment') {
            return breadcrumbTranslation('management-organisation-equipment')();
          }

          if (tab === 'managed') {
            return breadcrumbTranslation('management-organisation-managed')();
          }

          return OrganisationBreadcrumb();
        },
        lazy: () => import('pages/Management/Organisations/[Organisation]'),
      },
      {
        path: 'organisations/:organisationId/users/add-user',
        breadcrumb: breadcrumbTranslation('management-add-user'),
        lazy: () => import('pages/Management/Users/AddUser'),
      },
      {
        path: 'organisations/:organisationId/users/:userId',
        breadcrumb: UserBreadcrumb,
        lazy: () => import('pages/Management/Users/[User]'),
      },
      {
        path: 'organisations/:organisationId/equipment/new',
        breadcrumb: breadcrumbTranslation('management-add-equipment'),
        lazy: () => import('pages/Management/Organisations/[Organisation]/Equipment/AddEquipment'),
      },
      {
        path: 'organisations/:organisationId/equipment/:equipmentId',
        breadcrumb: EquipmentBreadcrumb,
        lazy: () => import('pages/Management/Organisations/[Organisation]/Equipment/[Equipment]'),
      },
      {
        path: 'organisations/:organisationId/managed/:managedOrganisationId',
        lazy: () => import('pages/Management/Organisations/[Organisation]'),
        children: [
          {
            index: true,
            breadcrumb: ManagedOrganisationBreadcrumb,
            lazy: () => import('pages/Management/Organisations/[Organisation]'),
          },
        ],
      },
      {
        path: 'my-organisations',
        breadcrumb: breadcrumbTranslation('management-my-organisations'),
        lazy: () => import('pages/Management/MyOrganisations'),
      },
      {
        path: 'greener-units/',
        breadcrumb: breadcrumbTranslation('management-assets'),
        lazy: () => import('pages/Management/GreenerUnits'),
      },
      {
        path: 'greener-units/:unitExternalIdentifier',
        breadcrumb: AssetBreadcrumb,
        lazy: () => import('pages/Management/GreenerUnits/[GreenerUnit]'),
      },
      {
        path: 'greener-units/add-greener-unit',
        breadcrumb: breadcrumbTranslation('management-add-assets'),
        lazy: () => import('pages/Management/GreenerUnits/AddGreenerUnit'),
      },
      {
        path: 'greener-eyes',
        breadcrumb: breadcrumbTranslation('management-eyes'),
        lazy: () => import('pages/Management/GreenerEyes'),
      },
      {
        path: 'greener-eyes/:greenerEyeExtId',
        breadcrumb: GreenerEyeBreadcrumb,
        lazy: () => import('pages/Management/GreenerEyes/[GreenerEye]'),
      },
      {
        path: 'greener-eyes/add-greener-eye',
        breadcrumb: breadcrumbTranslation('management-add-eye'),
        element: (
          <RequirePermission permission="greenerEye:create" redirectTo="/">
            <AddGreenerEye />
          </RequirePermission>
        ),
      },
      {
        path: 'watcher-types',
        breadcrumb: breadcrumbTranslation('management-watcher-types'),
        lazy: () => import('pages/Management/WatcherTypes'),
      },
      {
        path: 'watcher-types/:watcherTypeId',
        breadcrumb: WatcherTypeBreadcrumb,
        lazy: () => import('pages/Management/WatcherTypes/[WatcherType]'),
      },
      {
        path: 'watcher-types/add-watcher-type',
        breadcrumb: breadcrumbTranslation('management-add-watcher-type'),
        element: (
          <RequirePermission permission="watcherType:create" redirectTo="/">
            <AddWatcherType />
          </RequirePermission>
        ),
      },
      {
        path: 'device-models',
        breadcrumb: breadcrumbTranslation('management-device-models'),
        lazy: () => import('pages/Management/DeviceModels'),
      },
      {
        path: 'device-models/:deviceModelId',
        breadcrumb: DeviceModelBreadcrumb,
        lazy: () => import('pages/Management/DeviceModels/[DeviceModel]'),
      },
      {
        path: 'device-models/add-device-model',
        breadcrumb: breadcrumbTranslation('management-add-device-model'),
        lazy: () => import('pages/Management/DeviceModels/AddDeviceModel'),
      },
      {
        path: 'fleets',
        breadcrumb: breadcrumbTranslation('management-fleets'),
        lazy: () => import('pages/Management/Fleets'),
      },
      {
        path: 'fleets/:fleetId',
        breadcrumb: FleetBreadcrumb,
        lazy: () => import('pages/Management/Fleets/[Fleet]'),
      },
      {
        path: 'fleets/add-fleet',
        breadcrumb: breadcrumbTranslation('management-add-fleet'),
        lazy: () => import('pages/Management/Fleets/AddFleet'),
      },
    ],
  },
];
